import { useEffect, useRef, useState } from 'react';
import { JSONObject } from '../types/types';
import { resolveStringExpression } from '../utils/json/resolveStringExpression';
import { debugLogger } from '../debug/DebugLogger';

const log = debugLogger.scope('editability');

const RESOLVE_DEBOUNCE_MS = 250;

export const getInitialEditable = (editable?: boolean | string): boolean => {
  if (typeof editable === 'string') {
    return false;
  }
  if (typeof editable === 'boolean') {
    return editable;
  }
  return true;
};

export const useEditableResolve = (
  editable: boolean | string | undefined,
  resolveData: JSONObject,
  // Field identity for debug logs only — logs are anonymous without it.
  debugId?: string
) => {
  const [resolvedEditable, setResolvedEditable] = useState<boolean>(() =>
    getInitialEditable(editable)
  );
  const [pending, setPending] = useState(() => typeof editable === 'string');
  const isFirstRun = useRef(true);
  const lastLoggedRef = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    if (typeof editable !== 'string') {
      setResolvedEditable(getInitialEditable(editable));
      setPending(false);
      return;
    }

    let cancelled = false;
    setPending(true);

    const delay = isFirstRun.current ? 0 : RESOLVE_DEBOUNCE_MS;
    isFirstRun.current = false;

    // Debounce: each dep change reschedules; resolve runs only after deps stop changing for RESOLVE_DEBOUNCE_MS.
    const timeoutId = setTimeout(() => {
      resolveStringExpression(editable, resolveData)
        .then((resolved: boolean) => {
          if (!cancelled) {
            const value = Boolean(resolved);
            if (lastLoggedRef.current !== value) {
              lastLoggedRef.current = value;
              /** @debugFeature editability Logs a resolved `editable` expression outcome for a form field — only when the value changed. */
              log('editable resolved', () => ({
                debugId,
                expression: editable,
                editable: value,
              }));
            }
            setResolvedEditable(value);
          }
        })
        .catch((error) => {
          if (!cancelled) {
            console.error(
              '[useEditableResolve] Failed to evaluate `editable` JSONata expression.',
              { expression: editable, error }
            );

            /** @debugFeature editability Logs a failed `editable` expression — unlike visibility, the field keeps its previous value (locked on first run, but a previously editable field stays editable). */
            log('editable expression failed, keeping previous value', () => ({
              debugId,
              expression: editable,
              error,
            }));
          }
        })
        .finally(() => {
          if (!cancelled) {
            setPending(false);
          }
        });
    }, delay);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [editable, resolveData]);

  return { editable: resolvedEditable, pending };
};
